import { FC, MouseEvent } from 'react';
import { useInView } from 'react-intersection-observer';
import { useTranslation } from 'react-i18next';
import { Icon } from '@kontentino/ui';
import { faSpinnerThird } from '@fortawesome/pro-regular-svg-icons/faSpinnerThird';
import { Asset } from 'types/Attachments';
import SimpleGrid from 'app/components/SimpleGrid';
import MediaLibraryDragSource from 'app/modules/mediaLibraryPage/components/dnd/MediaLibraryDragSource';
import {
  AlbumDropResult,
  MediaLibraryDragItem,
} from 'app/modules/mediaLibraryPage/types/Dnd';
import AssetCard from './AssetCard';

const DEFAULT_COLUMNS = 6;

export function getDraggedAssetIds(
  assetId: string,
  selectedIds: string[],
): string[] {
  return selectedIds.length > 1 && selectedIds.includes(assetId)
    ? selectedIds
    : [assetId];
}

interface Props {
  assets: Asset[];
  totalCount?: number;
  selectedAssetIds: Set<string>;
  onSelectAsset(asset: Asset, event: MouseEvent): void;
  onOpenAsset(asset: Asset): void;
  columns?: number;
  onReachEnd?(): void;
  isLoadingMore?: boolean;
  onRenameAsset?(asset: Asset): void;
  onMoveAsset?(asset: Asset): void;
  onDownloadAsset?(asset: Asset): void;
  onSetAssetAsCover?(asset: Asset): void;
  onDeleteAsset?(asset: Asset): void;
  canDeleteAsset?(asset: Asset): boolean;
  showCheckboxes?: boolean;
  drag?: {
    sourceAlbumId: string;
    onDragEnd(item: MediaLibraryDragItem, result: AlbumDropResult | null): void;
  };
}

const AssetGrid: FC<Props> = ({
  assets,
  totalCount,
  selectedAssetIds,
  onSelectAsset,
  onOpenAsset,
  columns = DEFAULT_COLUMNS,
  onReachEnd,
  isLoadingMore,
  onRenameAsset,
  onMoveAsset,
  onDownloadAsset,
  onSetAssetAsCover,
  onDeleteAsset,
  canDeleteAsset,
  showCheckboxes = true,
  drag,
}) => {
  const { t } = useTranslation();
  const { ref } = useInView({
    threshold: 0,
    onChange: (inView) => {
      if (inView) onReachEnd?.();
    },
  });

  return (
    <>
      {totalCount !== undefined && (
        <h2 className="tw-mb-3 tw-text-md tw-font-semibold tw-text-grayscale-180">
          {t('mediaLibrary.page.folder.sectionFiles')}{' '}
          <span className="tw-text-grayscale-100">{totalCount}</span>
        </h2>
      )}
      <SimpleGrid
        columns={columns}
        gap={20}
        style={{ rowGap: 24 }}
        data-cy="media-library-page_grid"
      >
        {assets.map((asset) => {
          const card = (
            <AssetCard
              key={asset.id}
              asset={asset}
              isSelected={selectedAssetIds.has(asset.id)}
              isInMultiSelection={
                selectedAssetIds.size > 1 && selectedAssetIds.has(asset.id)
              }
              showCheckbox={showCheckboxes}
              onSelect={onSelectAsset}
              onOpen={onOpenAsset}
              onRename={onRenameAsset}
              onMove={onMoveAsset}
              onDownload={onDownloadAsset}
              onSetAsCover={onSetAssetAsCover}
              onDelete={
                onDeleteAsset && (canDeleteAsset?.(asset) ?? true)
                  ? onDeleteAsset
                  : undefined
              }
            />
          );
          if (!drag) return card;
          return (
            <MediaLibraryDragSource
              key={asset.id}
              item={{
                kind: 'assets',
                asset,
                sourceAlbumId: drag.sourceAlbumId,
                assetIds: getDraggedAssetIds(
                  asset.id,
                  Array.from(selectedAssetIds),
                ),
              }}
              onDragEnd={drag.onDragEnd}
            >
              {card}
            </MediaLibraryDragSource>
          );
        })}
      </SimpleGrid>
      {isLoadingMore && (
        <div
          className="tw-flex tw-items-center tw-justify-center tw-py-6 tw-text-grayscale-100"
          data-cy="media-library-page_grid_loading-more"
        >
          <Icon icon={faSpinnerThird} className="tw-animate-spin" />
          <span className="tw-ml-2 tw-text-sm">{t('loading')}</span>
        </div>
      )}
      {onReachEnd && (
        <div
          ref={ref}
          className="tw-h-2"
          data-cy="media-library-page_grid_sentinel"
        />
      )}
    </>
  );
};

export default AssetGrid;
